import React, { useState } from 'react';
import {
  Button,
  Chip,
  CircularProgress,
  Menu,
  MenuItem,
  Paper,
  TextField,
} from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import SyncIcon from '@mui/icons-material/Sync';

const ProductSelectionsHeader = ({
  rowCount,
  globalFilter,
  onGlobalFilterChange,
  onResetFilters,
  refreshSelectionsLabel,
  refreshSelectionsDisabled,
  refreshSelectionsPending,
  onRefreshSelections,
  onOpenCompare,
  compareDisabled,
  selectedRowsCount,
  onCopySelectedTsv,
  onCopySelectedJson,
  onStageSelected,
  onSyncSelected,
  syncDisabled,
  onExportVisibleCsv,
  onExportSelectedCsv,
  onExportVmtTemplate,
  hideRefreshSelections = false,
  hideCompare = false,
  hideExportVisibleCsv = false,
  hideExportVmtTemplate = false,
  hideGlobalSearch = false,
  hideGlobalReset = false,
}) => {
  const [copyAnchorEl, setCopyAnchorEl] = useState(null);
  const copyMenuOpen = Boolean(copyAnchorEl);
  const copyDisabled = !selectedRowsCount;
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const exportMenuOpen = Boolean(exportAnchorEl);

  const handleCopyMenuOpen = (event) => {
    if (copyDisabled) return;
    setCopyAnchorEl(event.currentTarget);
  };

  const handleCopyMenuClose = () => {
    setCopyAnchorEl(null);
  };

  const handleExportMenuOpen = (event) => {
    setExportAnchorEl(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportAnchorEl(null);
  };

  const handleCopyTsv = () => {
    onCopySelectedTsv?.();
    handleCopyMenuClose();
  };

  const handleCopyJson = () => {
    onCopySelectedJson?.();
    handleCopyMenuClose();
  };

  const handleExportVisible = () => {
    onExportVisibleCsv?.();
    handleExportMenuClose();
  };

  const handleExportSelected = () => {
    onExportSelectedCsv?.();
    handleExportMenuClose();
  };

  const handleExportVmt = () => {
    onExportVmtTemplate?.();
    handleExportMenuClose();
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 1,
        p: 1.25,
        borderRadius: 3,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <Chip label={`Rows: ${rowCount}`} color="primary" variant="outlined" size="small" />
      {hideGlobalSearch ? null : (
        <TextField
          size="small"
          placeholder="Global search…"
          value={globalFilter}
          onChange={(e) => onGlobalFilterChange?.(e.target.value)}
          inputProps={{ 'data-testid': 'selections-filter-global' }}
          sx={{ minWidth: 240 }}
        />
      )}
      {hideGlobalReset ? null : (
        <Button size="small" variant="outlined" startIcon={<FilterListIcon />} onClick={onResetFilters}>
          Reset
        </Button>
      )}
      {hideRefreshSelections ? null : (
        <Button
          size="small"
          variant="outlined"
          startIcon={refreshSelectionsPending ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
          onClick={onRefreshSelections}
          disabled={refreshSelectionsDisabled}
        >
          {refreshSelectionsLabel}
        </Button>
      )}

      {hideCompare ? null : (
        <Button
          size="small"
          variant="outlined"
          startIcon={<CompareArrowsIcon />}
          onClick={onOpenCompare}
          disabled={compareDisabled}
        >
          Compare
        </Button>
      )}
      <Button
        size="small"
        variant="outlined"
        startIcon={<ContentCopyIcon />}
        onClick={handleCopyMenuOpen}
        disabled={copyDisabled}
      >
        Copy
      </Button>
        <Menu
          anchorEl={copyAnchorEl}
          open={copyMenuOpen}
          onClose={handleCopyMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <MenuItem onClick={handleCopyTsv} disabled={copyDisabled}>
            Copy Selected TSV
          </MenuItem>
        <MenuItem onClick={handleCopyJson} disabled={copyDisabled}>
          Copy Selected JSON
        </MenuItem>
      </Menu>
      <Button
        size="small"
        variant="outlined"
        startIcon={<DownloadIcon />}
        onClick={handleExportMenuOpen}
        disabled={hideExportVisibleCsv && hideExportVmtTemplate && !selectedRowsCount}
      >
        Export
      </Button>
      <Menu
        anchorEl={exportAnchorEl}
        open={exportMenuOpen}
        onClose={handleExportMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItem onClick={handleExportVisible} disabled={hideExportVisibleCsv}>
          Export Visible CSV
        </MenuItem>
        <MenuItem onClick={handleExportSelected} disabled={!selectedRowsCount}>
          Export Selected CSV
        </MenuItem>
        <MenuItem onClick={handleExportVmt} disabled={hideExportVmtTemplate || !selectedRowsCount}>
          Export VMT Template
        </MenuItem>
      </Menu>
      <Button
        size="small"
        startIcon={<SendIcon />}
        onClick={onStageSelected}
        disabled={!selectedRowsCount}
        variant="contained"
      >
        Stage Selected
      </Button>
      <Button
        size="small"
        startIcon={<SyncIcon />}
        onClick={onSyncSelected}
        disabled={syncDisabled}
        variant="contained"
        data-testid="sync-di-button"
      >
        Sync DPE to DI
      </Button>

    </Paper>
  );
};

export default ProductSelectionsHeader;
